// Core dependencies
import { ITEM_STATUSES, ITEM_KINDS } from '@updater-core/constants/items';
import optimizely from '@updater-core/interactors/shell/optimizely';

// Local dependencies
import { PackingSuppliesExperiments } from './types/constants';

export function submit({ store, moveItems }) {
  return async () => {
    let result = {};
    const currentState = store.getState();
    const currentIndex = currentState.currentFormIndex || 0;
    // mark as completed in second page while we connect to the third party web hooks
    if (currentIndex === 1) {
      result = await markStep({
        store,
        actions: moveItems.actions,
        status: ITEM_STATUSES.COMPLETED,
      })('submit_appointment');
    }

    store.setState({
      ...currentState,
      currentFormIndex: currentIndex + 1,
      serverError: undefined,
    });

    return result;
  };
}

export function init({ store }) {
  return async ({
    currentFormIndex: formIndex,
  }: {
    currentFormIndex: string | undefined;
  }) => {
    const currentState = store.getState();

    store.setState({
      ...currentState,
      currentFormIndex:
        // if not packingKit selected return to make the selection
        formIndex && parseInt(formIndex) > 0 && !currentState.packingKit
          ? 0
          : formIndex,
    });
  };
}

export function update({ store }) {
  return ({ formData }) => {
    const currentState = store.getState();

    store.setState({
      ...currentState,
      ...formData,
    });
  };
}

export function navigateTo({ store }) {
  return (newIndex) => {
    const prevState = store.getState();
    store.setState({
      ...prevState,
      currentFormIndex: newIndex,
      serverError: undefined,
    });
  };
}

export function markStep({ store, actions, status }) {
  return async (label) => {
    const result = await actions.setStepStatus(
      ITEM_KINDS.PACKING_SUPPLIES,
      status,
      label
    );
    store.setState({
      status,
    });
    return result;
  };
}

/**
 * @function
 * @name initializeVariation
 * @description get optimizely type card variation and set it into the store
 * @param {Object} store interactor store
 * @returns {async function} async function that set the variation state
 */
export function initializeVariation({ store }) {
  store.setState({
    loadingExperiment: true,
  });
  return async () => {
    const variation = await optimizely.actions.getVariation(
      PackingSuppliesExperiments.CARD_EXPERIMENT
    );
    store.setState({
      variation,
      loadingExperiment: false,
    });
  };
}

/**
 * @function
 * @name initializeIntroPageVariation
 * @description get optimizely intro page variation and set it into the store
 * @param {Object} store interactor store
 * @returns {async function} async function that set the variation state
 */
export function initializeIntroPageVariation({ store }) {
  store.setState({
    loadingIntroPageExperiment: true,
  });
  return async () => {
    const introPageVariation = await optimizely.actions.getVariation(
      PackingSuppliesExperiments.INTRO_PAGE_EXPERIMENT
    );

    store.setState({
      introPageVariation,
      loadingIntroPageExperiment: false,
    });
  };
}

/**
 * @function
 * @name initializeEntryPointVariation
 * @description get optimizely entry point variation and set it into the store
 * @param {Object} store interactor store
 * @returns {async function} async function that set the variation state
 */
export function initializeEntryPointVariation({ store }) {
  store.setState({
    loadingEntryPointExperiment: true,
  });
  return async () => {
    const entryPointVariation = await optimizely.actions.getVariation(
      PackingSuppliesExperiments.ENTRY_POINT_EXPERIMENT
    );

    store.setState({
      entryPointVariation,
      loadingEntryPointExperiment: false,
    });
  };
}


/**
 * @function
 * @name initializeStarterKitVariation
 * @description get optimizely starter kit variation and set it into the store
 * @param {Object} store interactor store
 * @returns {async function} async function that set the variation state
 */
 export function initializeStarterKitVariation({ store }) {
  store.setState({
    loadingStarterKitExperiment: true,
  });
  return async () => {
    const starterKitVariation = await optimizely.actions.getVariation(
      PackingSuppliesExperiments.STARTER_KIT_EXPERIMENT
    );

    store.setState({
      starterKitVariation,
      loadingStarterKitExperiment: false,
    });
  };
}
